import { useCallback, useRef } from 'react';

import { Button, Typography } from '@mui/material';
import { ConfirmationDialog } from '@truvhq/ui';
import { useModal } from 'react-modal-hook';

import type { UnresolvedIncident } from 'api/types';
import { ModalType } from 'shared/lib/analytics/modalTypes';
import { useTrackedShowModal } from 'shared/lib/analytics/useTrackedShowModal';
import { formatDate } from 'shared/lib/datetime/formatDate';
import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';

const getIncidentDescription = (incident: UnresolvedIncident): string => {
  const { scheduled_for, scheduled_until, created_at } = incident;

  if (scheduled_for && scheduled_until) {
    return `Full functionality will be restored at ${formatDate(scheduled_until, false, 'h:mm a')}.`;
  }

  return `Possible performance degradation since ${formatDate(created_at, false, 'h:mm a')}.`;
};

const getIncidentTitle = (incident: UnresolvedIncident): string => {
  const { scheduled_for, scheduled_until } = incident;

  if (scheduled_for && scheduled_until) {
    return 'Planned maintenance in progress';
  }

  return 'Incident investigation in progress';
};

export const useIncidentDialog = () => {
  const incidentRef = useRef<UnresolvedIncident | null>(null);

  const [showModal, hideModal] = useModal(({ in: open }) => {
    const incident = incidentRef.current;

    if (!incident) {
      return null;
    }

    return (
      <ConfirmationDialog
        actions={
          <Button href={incident.shortlink} target="_blank" rel="noopener noreferrer" variant="contained">
            See details
          </Button>
        }
        content={<Typography>{getIncidentDescription(incident)}</Typography>}
        open={open}
        title={getIncidentTitle(incident)}
        onClose={hideModal}
      />
    );
  }, []);

  const showModalWithAnalytics = useTrackedShowModal(showModal, ModalType.STATUS_PAGE_INCIDENT);

  const showIncidentDialog = useCallback(
    (incident: UnresolvedIncident) => {
      const lastShownId = localStorageService.get(StorageKeys.LAST_SHOWN_INCIDENT_ID);
      if (lastShownId === incident.id) {
        return;
      }

      incidentRef.current = incident;
      localStorageService.set(StorageKeys.LAST_SHOWN_INCIDENT_ID, incident.id);
      showModalWithAnalytics();
    },
    [showModalWithAnalytics],
  );

  return {
    showIncidentDialog,
  };
};
